/* eslint-disable react-hooks/exhaustive-deps */
import { css } from 'aphrodite';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import {
	IPhoneCall,
	ITranscription,
	ITranscriptionSpeakerLines,
	PhoneCallRecordingTranscriptionStatus,
	TranscriptionStatus,
} from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { brandPrimary } from '../../../../web/styles/colors';
import { TranscriptionEventsContext } from '../../../entities/PhoneCall/TranscriptionEventsProvider';
import { useTranscription } from '../../../entities/PhoneCall/useTranscription';
import { FetchStatus } from '../../../entities/utils/useStatesOnFetch';
import { TransparentButton } from '../../TransparentButton';
import { TranscriptionIcon } from '../../svgs/icons/TranscriptionIcon';
import { styleSheet } from '../styles';
import { AdherenceTranscript } from './AdherenceTranscript';
import { SpeakerLinesComponent } from './SpeakerLinesComponent';

interface IProps {
	phoneCall: IPhoneCall;
	transcriptionId: string;
	onOpenAIFeedback?: (phoneCallId: string, transcriptionId: string) => void;
}

export const CallDetailsTranscription: React.FC<IProps> = ({ phoneCall, transcriptionId, onOpenAIFeedback }) => {
	const phoneCallId = phoneCall.id;

	const [transcriptionStatus, setStatus] = useState<TranscriptionStatus>(TranscriptionStatus.Unknown);

	const [currentTranscriptionId, setTranscriptionId] = useState<string>(transcriptionId);

	const [showAdherence, setShowAdherence] = useState(false);

	const userSession = useUserSession();

	const { data, getTranscription, createTranscription, status } = useTranscription<ITranscription>();
	const speakerLines = useTranscription<ITranscriptionSpeakerLines>();

	const { eventPayload } = useContext(TranscriptionEventsContext);

	const handleTranscribeRequest = (id: string) => {
		if (!currentTranscriptionId) {
			setTranscriptionId(id);
		}
		getTranscription(id).then(response => {
			if (!response) return;
			const { value } = response;

			if (!value) return;

			if (value.status === TranscriptionStatus.Completed) {
				setStatus(value.status);
				speakerLines.getTranscription(id, true);
				return;
			}
			setStatus(value.status);
		});
	};

	useEffect(() => {
		if (transcriptionId) {
			setTranscriptionId(transcriptionId);
			handleTranscribeRequest(transcriptionId);
		}
	}, [transcriptionId]);

	useEffect(() => {
		if (eventPayload) {
			handleTranscribeRequest(currentTranscriptionId);
		}
	}, [eventPayload]);

	const handleCreate = () =>
		createTranscription(phoneCallId).then(response => {
			if (!response) return;
			const { value } = response;

			if (!value || !value.id) return;

			handleTranscribeRequest(value.id);
		});

	const handleOpenAIFeedbackClick = () => {
		// In-place adherence view supersedes the legacy onOpenAIFeedback callback when an AI conversation exists.
		if (phoneCall.aiConversationId) {
			setShowAdherence(true);
			return;
		}
		if (onOpenAIFeedback && currentTranscriptionId && phoneCallId) {
			onOpenAIFeedback(phoneCallId, currentTranscriptionId);
		}
	};

	const isLoadingTranscription = status === FetchStatus.Working;
	const isLoadingSpeakerLines = speakerLines.status === FetchStatus.Working;

	const isFailed = transcriptionStatus === TranscriptionStatus.Failed;
	const isInProgress = transcriptionStatus === TranscriptionStatus.InProgress;
	const isCompleted = transcriptionStatus === TranscriptionStatus.Completed;

	const showTranscribe = isFailed || (!currentTranscriptionId && !isInProgress);

	const recordingTranscriptionStatus = phoneCall.recordingTranscriptionStatus;
	const isNotAvailable =
		recordingTranscriptionStatus === PhoneCallRecordingTranscriptionStatus.RecordingValidationFailure ||
		recordingTranscriptionStatus === PhoneCallRecordingTranscriptionStatus.TranscriptionFailure;

	const canTranscribe = userSession.account.features.aida.transcriptionOnDemandEnabled && !isNotAvailable;

	const renderTranscribeButton = canTranscribe ? (
		<TransparentButton className={css(styleSheet.regularButton)} onClick={handleCreate}>
			<TranscriptionIcon /> Transcribe
		</TransparentButton>
	) : (
		<div className={css(styleSheet.regularButton)}>
			{isNotAvailable ? 'Unable to transcribe this recording' : 'Transcription not available'}
		</div>
	);

	return (
		<div className={css(styleSheet.sectionWrapper)}>
			<div className={css(styleSheet.sectionTitle)}>Call Transcription</div>
			<div className={css(styleSheet.transcriptionContainer)}>
				{showTranscribe && !isLoadingTranscription && renderTranscribeButton}

				<div>{isLoadingTranscription && <LoadingSpinner type='small' />}</div>

				{!showTranscribe && !isLoadingTranscription && (
					<>
						{showAdherence ? (
							<AdherenceTranscript phoneCall={phoneCall} onBack={() => setShowAdherence(false)} />
						) : (
							<>
								<SpeakerLinesComponent
									isLoading={isLoadingSpeakerLines}
									isRead={data?.read}
									isSpeakerLinesReady={speakerLines.status === FetchStatus.Done}
									isTranscriptionCompleted={transcriptionStatus === TranscriptionStatus.Completed}
									speakerLines={speakerLines?.data?.speakerLines}
									transcriptionId={transcriptionId}
								/>
								{isCompleted && currentTranscriptionId && (phoneCall.aiConversationId || onOpenAIFeedback) && (
									<div className={css(styleSheet.transcriptionContentFooter)}>
										<TransparentButton className={css(styleSheet.regularButton)} onClick={handleOpenAIFeedbackClick}>
											<SparklesIcon style={{ color: brandPrimary }} /> See AI Feedback
										</TransparentButton>
									</div>
								)}
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
};
